/* What a render error leaves on screen: a stated failure in place of the part
 * that threw, rather than React unmounting everything to a blank document - a
 * page that reads exactly like one with no work on it (AR-78).
 *
 * A class because React has no hook for this: getDerivedStateFromError is a
 * class component's alone. React Router's errorElement is the router's version
 * of the same thing and needs its data router; the route table is declarative
 * (App.tsx), and moving every route onto createBrowserRouter for this alone
 * was rejected.
 *
 * It catches what React catches: a throw while rendering, in a lifecycle
 * method or in an effect. A throw in an event handler or a rejected promise
 * never unmounts the tree, so it is not this failure, and a query's failure is
 * TanStack Query's isError, shown where it is read.
 *
 * Used at both levels, which are not alternatives:
 * - AppShell wraps the screen in ScreenBoundary, so the header, the nav and
 *   the chosen tenant survive a screen that throws and the user can go
 *   somewhere else.
 * - main.tsx wraps the whole app, for a throw in the shell itself, where no
 *   nav is left to offer and a reload is the way forward. field/main.tsx
 *   wraps the field app the same way, and both through AppBoundary.
 *
 * NOTHING IS REPORTED. React logs the error to the browser's console and no
 * further, so a frontend crash reaches nobody but the person who saw it.
 */
import { Component, type ReactNode } from "react";
import { ErrorState } from "./EmptyState";

type Props = {
  children: ReactNode;
  /* What replaces the children once one throws, given a reset that renders
   * them again. */
  fallback: (reset: () => void) => ReactNode;
  /* A caught error is cleared when this changes. ScreenBoundary passes the
   * path, so leaving the broken screen shows the next one. */
  resetKey?: unknown;
};

type State = { failed: boolean; resetKey: unknown };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, resetKey: this.props.resetKey };

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  // Compared here rather than passed as a `key`: a key remounts the children
  // whenever it changes, and the path changes on navigation the screens
  // deliberately survive - a move between a tree and a pole keeps
  // RecordDetail's viewers mounted (App.tsx). This touches only a boundary
  // that has caught something.
  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    return props.resetKey === state.resetKey ? null : { failed: false, resetKey: props.resetKey };
  }

  reset = () => this.setState({ failed: false });

  render() {
    return this.state.failed ? this.props.fallback(this.reset) : this.props.children;
  }
}

/* The boundary around AppShell's screen, reset by the path. Here rather than
 * spelled out in AppShell so the test renders this exact wrapper, and a
 * missing path fails tsc rather than leaving a broken screen broken after the
 * user navigates away. */
export function ScreenBoundary({ path, children }: { path: string; children: ReactNode }) {
  return (
    <ErrorBoundary resetKey={path} fallback={(retry) => <FailedPage retry={retry} />}>
      {children}
    </ErrorBoundary>
  );
}

/* The boundary around a whole app, whose retry reloads the page. One component
 * so the web app and the field app show the same failure. */
export function AppBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundary fallback={() => <FailedPage retry={() => window.location.reload()} />}>{children}</ErrorBoundary>;
}

/* What both levels show in place of what threw. Retry is the caller's: a
 * screen renders again, and a shell that threw reloads the page. */
export function FailedPage({ retry }: { retry: () => void }) {
  return (
    <div className="page">
      <ErrorState onRetry={retry} />
    </div>
  );
}
